/* eslint-disable no-unused-vars */
/* Dependencies */
import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

/* Translations */
import { useTranslation } from 'react-i18next';

/* Components */
import GeneralInformationStep from './steps/GeneralInfo';
import PermissionsStep from './steps/Permissions';
import AssingCentersAndClassroomsStep from './steps/AssingCentersAndClassrooms';

/* Styled Components */
import { StylesAddMemberWrapper, PreviousBtn, NextBtn, Footer } from './styles';

/* Services, Hooks, Constants */

export interface AddTeacherViewProps {
  onFinishFlow?: (arg?: any) => any;
}

interface IFlow {
  [key: string]: any;
}

interface IStep {
  title: string;
  view: ComponentType<any>;
  status: string;
}

interface IState {
  currentStepIndex: number;
  steps?: IStep[];
  isCompleted?: boolean;
}

export interface IFlowStep {
  flowType?: string;
  nextStep?: (arg?: any) => {};
  previousStep?: (arg?: any) => {};
  formData?: any;
  renderFooter?: any;
  centers: any[];
  activity: any;
}

export interface IFormData {
  centers?: any[];
  weeks?: any[];
}

const ACT_FLOWS = {
  TEACHERS: 'teachers',
};

const GeneralInformation = {
  view: GeneralInformationStep,
};
const Permissions = {
  view: PermissionsStep,
};
const AssingCentersAndClassrooms = {
  view: AssingCentersAndClassroomsStep,
};

const FlowSteps: IFlow = {
  teachers: [GeneralInformation, Permissions, AssingCentersAndClassrooms],
};

const initialState = {
  currentStepIndex: 0,
  steps: [],
  isCompleted: false,
};

const reducer = (state: IState, action: any): IState => {
  switch (action.type) {
  case 'RESET_STEPS':
    return {
      ...state,
      currentStepIndex: initialState.currentStepIndex,
      isCompleted: initialState.isCompleted,
    };
  case 'INIT_STEPS':
    return { ...state, steps: action.steps };
  case 'NEXT_STEP':
    return { ...state, currentStepIndex: state.currentStepIndex + 1 };
  case 'PREV_STEP':
    return {
      ...state,
      currentStepIndex: state.currentStepIndex - 1,
      isCompleted: false,
    };
  case 'GO_TO_STEP':
    return {
      ...state,
      currentStepIndex: action.stepIndex,
      isCompleted: action.isCompleted,
    };
  case 'FINISH_FLOW':
    return { ...state, isCompleted: true };
  default:
    throw new Error();
  }
};

const AddTeacherView: React.FC<AddTeacherViewProps> = ({ onFinishFlow }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<any>({
    general_info: {
      name: '',
      lastname: '',
      email: '',
      avatar: {},
    },
    permissions: [],
  });
  const [flowType] = useState<string>(ACT_FLOWS.TEACHERS);

  const [
    { steps = [], currentStepIndex },
    dispatch,
  ] = useReducer(reducer, initialState);

  const initSteps = useCallback(() => {
    const fsteps = FlowSteps[flowType];

    dispatch({ type: 'INIT_STEPS', steps: [...fsteps] });
    // eslint-disable-next-line
  }, [flowType]);

  const isTheLastStep = useMemo(() => currentStepIndex === steps.length - 1, [
    currentStepIndex,
    steps.length,
  ]);

  const hasNext = useMemo(() => !(typeof steps[currentStepIndex] === 'undefined'), [currentStepIndex, steps]);

  const nextStep = (values: {}) => {
    setFormData({
      ...formData,
      ...values,
    });

    if (hasNext) {
      dispatch({ type: 'NEXT_STEP' });
    } else if (onFinishFlow) {
      dispatch({ type: 'FINISH_FLOW' });
    }

    return false;
  };

  const moveBack = () => dispatch({ type: 'PREV_STEP' });

  const handleCompleteButtonClick = (values: any) => {
    setFormData({ ...formData, ...values });

    if (onFinishFlow) {
      onFinishFlow({ ...formData, ...values });
    }
  };

  const handleBtnContinue = (values: any) => {
    return nextStep(values);
  };

  useEffect(() => {
    initSteps();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    initSteps();
    /* eslint-disable-next-line */
  }, [flowType]);

  const CurrentStep = steps[currentStepIndex];
  if (!CurrentStep) return null;

  return (
    <StylesAddMemberWrapper>
      <CurrentStep.view
        formData={formData}
        flowType={flowType}
        nextStep={nextStep}
        renderFooter={(isValid: boolean, data: any = {}) => (
          <Footer alignRight={currentStepIndex === 0}>
            {
              currentStepIndex !== 0 && (
                <PreviousBtn
                  type="button"
                  disabled={currentStepIndex === 0}
                  onClick={() => currentStepIndex >= 1 && moveBack()}
                >
                  {t('settings:PREVIOUS')}
                </PreviousBtn>
              )
            }
            <NextBtn
              type="button"
              disabled={!isValid}
              onClick={() =>
                isTheLastStep
                  ? handleCompleteButtonClick(data)
                  : handleBtnContinue(data)
              }
            >
              {t(isTheLastStep ? 'settings:ADD' : 'settings:NEXT')}
            </NextBtn>
          </Footer>
        )}
      />
    </StylesAddMemberWrapper>
  );
};

export default AddTeacherView;
